import React, { useState, useEffect } from 'react';
import { FaTimes, FaChevronRight, FaSearch, FaCheckSquare, FaSquare } from 'react-icons/fa';

export default function DemographicsDropdown({
  filters, // Current filter state from DashboardApp
  onFilterChange, // Function to update filters in DashboardApp
  onClose, // Function to close the dropdown
  allDemographicsData, // All fetched demographics data
}) {

  // State to manage visibility of nested filter options (Age, Gender, etc.)
  const [openCategory, setOpenCategory] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  // Mapping of internal filter keys to display labels
  const categoryLabels = {
    age: 'Age',
    automatedAccounts: 'Automated accounts',
    familyStatus: 'Family Status',
    gender: 'Gender',
    individualBusiness: 'Individual Business',
    nonBinaryGender: 'Non-binary Gender',
    occupations: 'Occupations',
  };

  const handleCategoryClick = (categoryKey) => {
    setOpenCategory(openCategory === categoryKey ? null : categoryKey);
    setSearchTerm(''); // Clear search when opening a new category
  };

  const handleCheckboxChange = (category, value) => {
    const currentFilters = filters[category] || [];
    const newFilters = currentFilters.includes(value)
      ? currentFilters.filter(item => item !== value)
      : [...currentFilters, value];
    onFilterChange({ ...filters, noFilter: false, [category]: newFilters }); // Set noFilter to false when a specific filter is applied
  };

  const handleNoFilterChange = () => {
    // When 'No filter on Demographics' is checked, reset all other filters
    onFilterChange({
      noFilter: !filters.noFilter,
      age: [],
      automatedAccounts: [],
      familyStatus: [],
      gender: [],
      individualBusiness: [],
      nonBinaryGender: [],
      occupations: [],
    });
  };
  
  // Get the data for the currently open category for rendering options
  const getCategoryOptions = (categoryKey) => {
    // Special handling for gender, which is not an array of objects
    if (categoryKey === 'gender') {
      return ['female', 'male'].map(g => ({ label: g.charAt(0).toUpperCase() + g.slice(1), value: g }));
    }
    // For other categories, assume the data is an array of objects with 'label' and 'value'
    return allDemographicsData?.[categoryKey]?.map(item => ({ label: item.label, value: item.label })) || [];
  };
  
  // Filter category options based on search term
  const filteredCategoryOptions = openCategory ? 
    getCategoryOptions(openCategory).filter(option => 
      option.label.toLowerCase().includes(searchTerm.toLowerCase())
    ) : [];

  // Determine if a filter category has any active selections (excluding noFilter)
  const isCategoryFiltered = (categoryKey) => {
    return filters[categoryKey] && filters[categoryKey].length > 0;
  };

  return (
    <div className="demographics-dropdown">
      <div className="dropdown-header">
        <h3>DEMOGRAPHICS</h3>
        {/* Three dots icon - can add onClick later */}
        <div className="three-dots-icon">...</div> 
      </div>

      {/* Active Filters Summary */}
      <div className="active-filters-summary">
        {filters.noFilter ? (
          'No Active Demographics'
        ) : (
          // You could display a summary of active filters here
          'Filters Applied...'
        )}
      </div>

      {/* Search Input */}
      <div className="search-input-container">
        <FaSearch />
        <input
          type="text"
          placeholder="Search Demographics"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      {/* Filter Options List */}
      <div className="filter-options-list">
        {/* No Filter Option */}
        <div className="filter-option-item" onClick={handleNoFilterChange}>
          <div className="checkbox-container">
            {filters.noFilter ? <FaCheckSquare color="blue" /> : <FaSquare />}
          </div>
          <span>No filter on Demographics</span>
          {/* No chevron for this option */}
        </div>

        {/* Other Filter Categories */}
        {Object.keys(categoryLabels).map(categoryKey => (
          <div key={categoryKey} className={`filter-option-item ${openCategory === categoryKey ? 'open' : ''} ${isCategoryFiltered(categoryKey) ? 'filtered' : ''}`} onClick={() => handleCategoryClick(categoryKey)}>
            <div className="checkbox-container">
               {/* Show checkmark if this category has active filters */}
               {isCategoryFiltered(categoryKey) ? <FaCheckSquare color="blue" /> : <FaSquare />}
            </div>
            <span>{categoryLabels[categoryKey]}</span>
            <FaChevronRight className="chevron-icon" />
          </div>
        ))}

        {/* Nested Options - Shown when a category is open */}
        {openCategory && ( 
          <div className="nested-options">
            {filteredCategoryOptions.length > 0 ? (
              filteredCategoryOptions.map(option => (
                <div key={option.value} className="filter-option-item nested" onClick={() => handleCheckboxChange(openCategory, option.value)}>
                   <div className="checkbox-container">
                      {filters[openCategory]?.includes(option.value) ? <FaCheckSquare color="blue" /> : <FaSquare />}
                   </div>
                   <span>{option.label}</span>
                   {/* No chevron for nested options */}
                </div>
              ))
            ) : (
              <div className="filter-option-item nested">No results found.</div>
            )}
          </div>
        )}

      </div>
    </div>
  );
} 